import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../api/client";
import { X, Play, Pause, Mic, MicOff, ChevronRight, Loader2, Headphones } from "lucide-react";

/**
 * Full-screen, voice-guided workout player.
 * For each exercise:
 *   1. Claude writes a warm cue → ElevenLabs speaks it in Sopfie's voice.
 *   2. The audio auto-plays.
 *   3. Afterwards, we listen (Web Speech API) for "done / next / finished / ready"
 *      — or the client taps the big button. Then we jump to the next exercise.
 *
 * Uses the existing /api/ai/coach-voice endpoint (per-exercise calls).
 */
export default function GuidedWorkout({ program, onClose }) {
  const exercises = program.workouts?.[0]?.exercises || [];
  const total = exercises.length;
  const [idx, setIdx] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [audioSrc, setAudioSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [listening, setListening] = useState(false);
  const [finished, setFinished] = useState(false);
  const audioRef = useRef(null);
  const recogRef = useRef(null);

  const current = exercises[idx];

  // Generate + play cue for the current exercise
  const speakExercise = async (exercise) => {
    if (!exercise) return;
    setLoading(true); setError(""); setTranscript(""); setAudioSrc("");
    stopListening();
    try {
      const parts = [];
      if (exercise.sets) parts.push(`${exercise.sets} sets`);
      if (exercise.reps) parts.push(`${exercise.reps} reps`);
      if (exercise.rest) parts.push(`rest ${exercise.rest}`);
      const context = [
        parts.join(" · ") || null,
        exercise.notes || null,
        `This is exercise ${idx + 1} of ${total}. After the cue, the client will say "done" or tap to continue.`,
      ].filter(Boolean).join(" · ");
      const { data } = await api.post("/ai/coach-voice", {
        exercise: exercise.name,
        context,
      });
      setTranscript(data.transcript);
      setAudioSrc(`data:audio/mpeg;base64,${data.audio_base64}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Say a farewell when the workout is complete
  const speakFarewell = async () => {
    setLoading(true); setError(""); setTranscript(""); setAudioSrc("");
    stopListening();
    try {
      const { data } = await api.post("/ai/coach-voice", {
        exercise: "End of workout",
        context: "The client has just finished the last exercise of their workout. Say a short, warm, grounded closing — something like 'Beautiful work. Take a moment to breathe, feel your body, and let everything settle. I'm so proud of you.' Do not use any pet names.",
      });
      setTranscript(data.transcript);
      setAudioSrc(`data:audio/mpeg;base64,${data.audio_base64}`);
      setFinished(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // When the user signals "done" → advance
  const advance = () => {
    if (loading) return;
    if (idx + 1 < total) {
      setIdx(idx + 1);
    } else if (!finished) {
      speakFarewell();
    } else {
      onClose?.();
    }
  };

  // Trigger generation whenever idx changes (first load + advance)
  useEffect(() => {
    if (!finished && current) speakExercise(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Try to autoplay when new audio arrives
  useEffect(() => {
    if (audioSrc && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [audioSrc]);

  // Speech recognition — start listening after audio ends
  const startListening = () => {
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) return;
      stopListening();
      const r = new SR();
      r.lang = "en-US";
      r.continuous = true;
      r.interimResults = true;
      r.onresult = (ev) => {
        const text = Array.from(ev.results).map((r) => r[0].transcript).join(" ").toLowerCase();
        if (/\b(done|next|finished|complete|continue|ready|go)\b/.test(text)) {
          stopListening();
          advance();
        }
      };
      r.onerror = () => {};
      r.onend = () => setListening(false);
      r.start();
      recogRef.current = r;
      setListening(true);
    } catch {
      // browser doesn't support it — button still works
    }
  };
  const stopListening = () => {
    try { recogRef.current?.stop(); } catch {}
    recogRef.current = null;
    setListening(false);
  };

  useEffect(() => () => stopListening(), []);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  if (total === 0) return null;

  const node = (
    <div className="fixed inset-0 z-[100] bg-gradient-to-br from-[#2a2724] via-[#322c27] to-[#3f342b] text-[color:var(--bg)] overflow-auto" data-testid="guided-workout">
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Headphones size={18} strokeWidth={1.5} className="text-[#E4B9A5]" />
            <span className="font-serif text-xl">Your session with Sopfie</span>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white p-2"
            data-testid="guided-workout-close"
            aria-label="End session"
          >
            <X size={22} strokeWidth={1.6} />
          </button>
        </header>

        <div className="flex-1 px-6 md:px-10 py-10 max-w-3xl mx-auto w-full">
          {/* Progress */}
          {!finished && (
            <>
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-3">
                Exercise {idx + 1} of {total}
              </p>
              <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#E4B9A5] transition-all duration-500"
                  style={{ width: `${((idx + 1) / total) * 100}%` }}
                />
              </div>
            </>
          )}

          <h1 className="font-serif text-4xl md:text-6xl font-light mt-8 leading-tight" data-testid="guided-workout-title">
            {finished ? "Beautifully done." : current?.name}
          </h1>
          {!finished && (current?.sets || current?.reps || current?.rest) && (
            <p className="mt-3 text-white/60 text-sm">
              {current.sets && <>{current.sets} sets </>}
              {current.reps && <>· {current.reps} reps </>}
              {current.rest && <>· rest {current.rest}</>}
            </p>
          )}

          {/* Loading state */}
          {loading && (
            <div className="mt-10 flex items-center gap-3 text-white/70">
              <Loader2 size={18} strokeWidth={1.6} className="animate-spin" />
              Sopfie is preparing her cue…
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-6 p-4 rounded-xl bg-red-900/30 border border-red-800/50 text-red-200 text-sm" data-testid="guided-workout-error">
              {error}
              <button onClick={() => speakExercise(current)} className="ml-3 underline">Retry</button>
            </div>
          )}

          {/* Transcript */}
          {transcript && (
            <p className="mt-8 text-lg md:text-xl font-serif italic leading-relaxed text-white/85" data-testid="guided-workout-transcript">
              "{transcript}"
            </p>
          )}

          {/* Audio player */}
          {audioSrc && (
            <div className="mt-6 flex items-center gap-4" data-testid="guided-workout-player">
              <button
                onClick={togglePlay}
                className="h-14 w-14 rounded-full bg-[#E4B9A5] text-[#2a2724] flex items-center justify-center hover:scale-105 transition-transform"
                aria-label={playing ? "Pause" : "Play"}
                data-testid="guided-workout-playbtn"
              >
                {playing ? <Pause size={18} strokeWidth={1.8} /> : <Play size={18} strokeWidth={1.8} className="translate-x-0.5" />}
              </button>
              <audio
                ref={audioRef}
                src={audioSrc}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); if (!finished) startListening(); }}
                className="flex-1 max-w-md"
                controls
                data-testid="guided-workout-audio"
              />
            </div>
          )}

          {/* Listening indicator */}
          {listening && (
            <div className="mt-6 flex items-center gap-3 text-white/70" data-testid="guided-workout-listening">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E4B9A5] opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#E4B9A5]" />
              </span>
              Listening — say "done" or "next" when you're ready
            </div>
          )}

          {/* Advance button */}
          {!loading && !error && (
            <div className="mt-10 flex flex-wrap items-center gap-3">
              {!finished ? (
                <>
                  <button
                    onClick={advance}
                    className="btn-primary !py-3 !px-6 !text-base"
                    data-testid="guided-workout-next"
                  >
                    {idx + 1 < total ? (
                      <>Done — next exercise <ChevronRight size={16} strokeWidth={1.8} /></>
                    ) : (
                      <>Finish workout <ChevronRight size={16} strokeWidth={1.8} /></>
                    )}
                  </button>
                  {!listening ? (
                    <button
                      onClick={startListening}
                      className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1.5"
                      data-testid="guided-workout-listen"
                    >
                      <Mic size={14} strokeWidth={1.6} /> Hands-free mode
                    </button>
                  ) : (
                    <button
                      onClick={stopListening}
                      className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1.5"
                      data-testid="guided-workout-stop-listen"
                    >
                      <MicOff size={14} strokeWidth={1.6} /> Stop listening
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={onClose}
                  className="btn-primary !py-3 !px-6 !text-base"
                  data-testid="guided-workout-end"
                >
                  Close session
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : null;
}
